import type { DjuSettings, LngLat, PeriodKey, Perimeter, PlanBuilding, RatioTable, UsageKey, WeatherStation } from './types';

// ─── Référentiels (VALEURS DE DÉMONSTRATION — à fournir par le métier) ─────────

export const USAGES: { key: UsageKey; label: string }[] = [
  { key: 'resCollectif', label: 'Résidentiel collectif' },
  { key: 'resIndividuel', label: 'Résidentiel individuel' },
  { key: 'tertiaire', label: 'Tertiaire' },
  { key: 'enseignement', label: 'Enseignement' },
  { key: 'sante', label: 'Santé' },
  { key: 'autre', label: 'Autre' },
];

export const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: 'avant1948', label: '< 1948' },
  { key: 'p1948_1974', label: '1948-1974' },
  { key: 'p1975_1999', label: '1975-1999' },
  { key: 'p2000_2012', label: '2000-2012' },
  { key: 'apres2012', label: '> 2012' },
];

export const USAGE_LABEL = Object.fromEntries(USAGES.map((u) => [u.key, u.label])) as Record<UsageKey, string>;
export const PERIOD_LABEL = Object.fromEntries(PERIODS.map((p) => [p.key, p.label])) as Record<PeriodKey, string>;

/** Ratios chauffage + ECS en kWh/m²/an — valeurs de démonstration. */
export const DEFAULT_RATIOS: RatioTable = {
  resCollectif: { avant1948: 190, p1948_1974: 210, p1975_1999: 160, p2000_2012: 120, apres2012: 70 },
  resIndividuel: { avant1948: 220, p1948_1974: 240, p1975_1999: 180, p2000_2012: 130, apres2012: 75 },
  tertiaire: { avant1948: 210, p1948_1974: 230, p1975_1999: 180, p2000_2012: 140, apres2012: 90 },
  enseignement: { avant1948: 170, p1948_1974: 185, p1975_1999: 150, p2000_2012: 115, apres2012: 70 },
  sante: { avant1948: 260, p1948_1974: 280, p1975_1999: 240, p2000_2012: 190, apres2012: 130 },
  autre: { avant1948: 150, p1948_1974: 160, p1975_1999: 130, p2000_2012: 100, apres2012: 60 },
};

/** Stations météo et DJU annuels base 18 °C — valeurs de démonstration. */
export const STATIONS: WeatherStation[] = [
  { id: 'montsouris', name: 'Paris-Montsouris (75)', dju: 2210 },
  { id: 'orly', name: 'Orly (94)', dju: 2380 },
  { id: 'bourget', name: 'Le Bourget (93)', dju: 2340 },
  { id: 'villacoublay', name: 'Vélizy-Villacoublay (78)', dju: 2450 },
  { id: 'roissy', name: 'Roissy-CDG (95)', dju: 2420 },
  { id: 'melun', name: 'Melun-Villaroche (77)', dju: 2490 },
];

export const DEFAULT_DJU: DjuSettings = { stationId: 'montsouris', reference: 2500 };

export const RATIO_MIN = 0;
export const RATIO_MAX = 1000;
export const isValidRatio = (v: number) => Number.isFinite(v) && v > RATIO_MIN && v <= RATIO_MAX;
export const isValidDju = (v: number) => Number.isFinite(v) && v >= 500 && v <= 5000;

export const cloneRatios = (r: RatioTable): RatioTable =>
  Object.fromEntries(USAGES.map((u) => [u.key, { ...r[u.key] }])) as RatioTable;

export function countModifiedRatios(r: RatioTable, ref: RatioTable = DEFAULT_RATIOS): number {
  let n = 0;
  for (const u of USAGES) for (const p of PERIODS) if (r[u.key][p.key] !== ref[u.key][p.key]) n += 1;
  return n;
}

export function periodOf(year: number): PeriodKey {
  if (year < 1948) return 'avant1948';
  if (year <= 1974) return 'p1948_1974';
  if (year <= 1999) return 'p1975_1999';
  if (year <= 2012) return 'p2000_2012';
  return 'apres2012';
}

// ─── Calcul de consommation ───────────────────────────────────────────────

export function stationOf(dju: DjuSettings): WeatherStation {
  return STATIONS.find((s) => s.id === dju.stationId) ?? STATIONS[0];
}

/**
 * Coefficient de correction climatique. FORMULE NON CONFIRMÉE PAR LE MÉTIER :
 * hypothèse « DJU station / DJU de référence ». Toute évolution se fait ici uniquement.
 */
export function djuCoefficient(dju: DjuSettings): number {
  return dju.reference > 0 ? stationOf(dju).dju / dju.reference : 1;
}

export interface BuildingConsumption {
  ratio: number;
  /** MWh/an */
  raw: number;
  coefficient: number;
  /** MWh/an */
  corrected: number;
}

export function buildingConsumption(b: PlanBuilding, ratios: RatioTable, dju: DjuSettings): BuildingConsumption {
  const ratio = ratios[b.usage][b.period];
  const raw = (ratio * b.heatedArea) / 1000;
  const coefficient = djuCoefficient(dju);
  return { ratio, raw, coefficient, corrected: raw * coefficient };
}

/** Consommation globale corrigée du périmètre, en MWh/an. */
export function totalConsumption(buildings: PlanBuilding[], ratios: RatioTable, dju: DjuSettings): number {
  const k = djuCoefficient(dju);
  return buildings.reduce((s, b) => s + (ratios[b.usage][b.period] * b.heatedArea) / 1000, 0) * k;
}

/** Classes de la légende carte (MWh/an corrigés) — couleurs de jeu de données (hex autorisé). */
export const CONSUMPTION_CLASSES = [
  { max: 25, label: '< 25', color: '#fde6c8' },
  { max: 75, label: '25 – 75', color: '#f9bb7d' },
  { max: 200, label: '75 – 200', color: '#ef6b26' },
  { max: 500, label: '200 – 500', color: '#c2410c' },
  { max: Infinity, label: '≥ 500', color: '#7c2d12' },
] as const;

// ─── Géométrie ─────────────────────────────────────────────────────────────

/** Ray casting — anneau ouvert ou fermé, coordonnées lng/lat. */
export function pointInPolygon([x, y]: LngLat, ring: LngLat[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function inPerimeter(b: PlanBuilding, perimeter: Perimeter): boolean {
  if (perimeter.kind === 'communes') return perimeter.communes.includes(b.commune);
  return perimeter.polygon.length >= 3 && pointInPolygon(b.centroid, perimeter.polygon);
}

export function boundsOf(points: LngLat[]): [LngLat, LngLat] {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of points) {
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  }
  return [[x0, y0], [x1, y1]];
}

export const closeRing = (ring: LngLat[]): LngLat[] => (ring.length ? [...ring, ring[0]] : ring);

// ─── Formats ───────────────────────────────────────────────────────────────

export const fmt = (n: number, digits = 0) =>
  n.toLocaleString('fr-FR', { maximumFractionDigits: digits, minimumFractionDigits: digits });
export const gwh = (mwh: number) => `${fmt(mwh / 1000, 1)} GWh/an`;
export const today = () => new Date().toLocaleDateString('fr-FR');
